import axios from 'axios'
import api from './api'
import type { PageAccess, UserRole } from '@/utils/pagePermissions'

export interface AuthUser {
  email: string
  name?: string | null
  picture?: string | null
  sub?: string | null
  role: UserRole
  is_super_admin: boolean
  permissions: Record<string, PageAccess>
}

const AUTH_ME_RETRY_DELAYS_MS = [1000, 2000]

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function isRetryableNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response
}

export async function getCurrentUser(): Promise<AuthUser> {
  let lastError: unknown

  for (let attempt = 0; attempt < AUTH_ME_RETRY_DELAYS_MS.length + 1; attempt += 1) {
    try {
      const response = await api.get<AuthUser>('/auth/me')
      return response.data
    } catch (error) {
      lastError = error

      if (!isRetryableNetworkError(error)) {
        throw error
      }

      const retryDelay = AUTH_ME_RETRY_DELAYS_MS[attempt]
      if (retryDelay === undefined) {
        break
      }

      await sleep(retryDelay)
    }
  }

  throw lastError
}

export function login(nextPath?: string): void {
  const next = nextPath && nextPath.startsWith('/') ? nextPath : '/'
  window.location.href = `/api/auth/login?next=${encodeURIComponent(next)}`
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout')
}
